// EditBadge.tsx
import React, { FunctionComponent, useState, useEffect } from 'react';
import styled from 'styled-components';
import Colors from '../../../ui/global/Colors';
import Fonts from '../../../ui/global/Fonts';
import Icon from '../../../ui/global/Icon';
import Logo from '../../../ui/components/Logo';
import ButtonMicro from '../../../ui/components/ButtonMicro';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGame } from '../../../backend/useGame';
import { captureEvent } from '../../../utils/analytics';
import { useGameMetrics } from '../../../backend/useGameMetrics';

const BadgeRoot = styled.div<{ $colorMode: 'light' | 'dark' }>`
  display: flex;
  align-items: center;
  width: fit-content;
  background-color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey1 : Colors.grey9};
  border: 1px solid ${Colors.transparent};
  border-radius: 8px;
  padding: 6px 8px 6px 12px;
  gap: 6px;
  cursor: pointer;

  &:hover {
    background-color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey1 : Colors.grey9};
    border: 1px solid ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey2 : Colors.grey8};
  }

  &:active {
    background-color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.white : Colors.black};
    border: 1px solid ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey2 : Colors.grey8};
  }
`;

const BadgeText = styled.span<{ $colorMode: 'light' | 'dark' }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-family: ${Fonts.lexendSemiLight.fontFamily};
  font-weight: ${Fonts.lexendSemiLight.fontWeight};
  font-size: 14px;
  color: ${({ $colorMode }) => $colorMode === 'light' ? Colors.grey8 : Colors.grey2};
  line-height: 1.2em;
  gap: 6px;
  user-select: none;
`;

interface EditBadgeProps {
  colorMode?: 'light' | 'dark';
  gameId: string;
  isMyGame: boolean;
  user: any;
}

const EditBadge: FunctionComponent<EditBadgeProps> = ({
  colorMode = 'light',
  gameId,
  isMyGame,
  user,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { remixGame } = useGame();
  const { incrementRemixes } = useGameMetrics();
  const [isOpen, setIsOpen] = useState(true);
  const [isCopying, setIsCopying] = useState(false);

  // useEffect(() => {
  //   console.log('isMyGame', isMyGame);
  //   if (isMyGame) {
  //     setIsOpen(false);
  //   } else {
  //     setIsOpen(true);
  //   }
  // }, [isMyGame]);

  const handleBadgeClick = async () => {
    if (!user) {
      const state = { from: location };
      navigate('/login', { state });
      return;
    }

    setIsCopying(true);
    try {
      const newGame = await remixGame(gameId);
      captureEvent('remixed game', { original_game_id: gameId, new_game_id: newGame.id });
      incrementRemixes(gameId);
      navigate(`/game/${newGame.id}`);
      setIsCopying(false);
    } catch (error) {
      console.error('Error remixing game:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <BadgeRoot $colorMode={colorMode} onClick={handleBadgeClick}>
      {!isCopying ? (
        <>
        <BadgeText $colorMode={colorMode}>
            Edit with
            <Logo text='right' size={14} colorMode={colorMode} />
        </BadgeText>
        <ButtonMicro 
          icon="delete" 
          size={14}
          borderRadius='100%'
          padding='2px'
          showBorder={false}
          colorMode={colorMode}
          onClick={(e) => {
            e.stopPropagation();
            // console.log('close clicked');
            setIsOpen(false);
          }}
        />
        </>
      ) : (
        <BadgeText $colorMode={colorMode}>
          <Icon icon="loading" size={14} color={colorMode === 'light' ? Colors.black : Colors.white} />
          Copying...
        </BadgeText>
      )}
    </BadgeRoot>
  );
};

export default EditBadge;